/**
 * Permission Loader Hook
 * Handles loading user permissions from API after authentication
 * 
 * NOTE: This hook has been temporarily disabled as part of auth service consolidation.
 * Permissions are now loaded during the login flow through consolidated services.
 * TODO: Re-implement using consolidated AuthenticationService or Redux thunks
 */

import { useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectIsAuthenticated, selectUser } from '../../../store/slices/authSlice';
import type { RootState } from '../../../store';

/**
 * Hook to automatically load user permissions after authentication
 */
export const usePermissionLoader = () => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state: RootState) => selectIsAuthenticated(state));
  const user = useSelector((state: RootState) => selectUser(state));
  useRef(false);
  const loadedRef = useRef(false);

  const loadPermissions = useCallback(async (_forceRefresh: boolean = false) => {
    // TODO: Implement permission loading through consolidated services
    console.log('Permission loading is handled during login flow');
    return;
  }, [isAuthenticated, user?.id, dispatch]);

  // Load permissions when user becomes authenticated
  useEffect(() => {
    if (isAuthenticated && user?.id) {
      // Permissions are loaded during login, no action needed
    } else {
      // Reset loaded state when user logs out
      loadedRef.current = false;
    }
  }, [isAuthenticated, user?.id, loadPermissions]);

  // Return function to manually reload permissions
  return {
    reloadPermissions: (forceRefresh?: boolean) => loadPermissions(forceRefresh),
    forceRefreshPermissions: () => loadPermissions(true),
    clearPermissionsCache: () => {
      loadedRef.current = false;
    }
  };
};

/**
 * Hook to check if permissions are loaded
 * Returns true if user is loaded (even if they have no permissions)
 */
export const usePermissionsLoaded = (): boolean => {
  const user = useSelector((state: RootState) => selectUser(state));
  const isAuthenticated = useSelector((state: RootState) => selectIsAuthenticated(state));
  // Consider permissions "loaded" if user exists and is authenticated
  // Even if systemPermissions array is empty, it means permissions were loaded (user just has none)
  return isAuthenticated && !!user && user.systemPermissions !== undefined;
};